import { useState, useEffect } from "react";
import { ArrowRight, Building2, TrendingUp, Users, BarChart3, CheckCircle, Calculator } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Slider } from "@/components/ui/slider";

const HeroB2B = () => {
  const [animatedPipeline, setAnimatedPipeline] = useState(2.4);
  const [animatedDeals, setAnimatedDeals] = useState(340);

  // Mini ROI Calculator state
  const [dealSize, setDealSize] = useState([25000]);
  const [monthlyLeads, setMonthlyLeads] = useState([15]);

  useEffect(() => {
    const pipelineInterval = setInterval(() => {
      setAnimatedPipeline(prev => parseFloat((prev + 0.1).toFixed(1)));
    }, 30000);

    const dealsInterval = setInterval(() => {
      setAnimatedDeals(prev => prev + Math.floor(Math.random() * 5) + 1);
    }, 25000);

    return () => {
      clearInterval(pipelineInterval);
      clearInterval(dealsInterval);
    };
  }, []);

  // Mini ROI calculation
  const currentRevenue = dealSize[0] * monthlyLeads[0] * 0.15 * 12;
  const projectedRevenue = dealSize[0] * 1.34 * monthlyLeads[0] * 1.5 * 0.22 * 12;
  const annualImpact = projectedRevenue - currentRevenue;

  const formatCurrency = (value: number) => {
    if (value >= 1000000) return `£${(value / 1000000).toFixed(1)}M`;
    return `£${(value / 1000).toFixed(0)}K`;
  };


  return (
    <section className="relative py-12 md:py-20 lg:py-28 overflow-hidden" style={{ background: 'var(--gradient-hero)' }}>
      {/* Subtle grid pattern overlay */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `linear-gradient(rgba(255,255,255,.1) 1px, transparent 1px),
                           linear-gradient(90deg, rgba(255,255,255,.1) 1px, transparent 1px)`,
          backgroundSize: '50px 50px'
        }} />
      </div>

      {/* Gradient orbs */}
      <div className="absolute inset-0 opacity-20">
        <div className="absolute top-20 left-10 w-96 h-96 bg-secondary rounded-full mix-blend-multiply filter blur-3xl animate-pulse" />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-accent rounded-full mix-blend-multiply filter blur-3xl animate-pulse" style={{ animationDelay: '2s' }} />
      </div>

      <div className="container mx-auto px-4 md:px-6 relative z-10">
        <div className="max-w-6xl mx-auto">
          {/* Top Badge */}
          <div className="text-center mb-6">
            <Badge variant="outline" className="bg-white/10 border-white/20 text-white px-4 py-1.5 text-sm">
              B2B Growth Infrastructure
            </Badge>
          </div>

          {/* Main Headline */}
          <div className="text-center mb-8">
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold mb-4 text-white leading-tight">
              Build Predictable
              <span className="block bg-gradient-to-r from-secondary via-accent to-secondary bg-clip-text text-transparent">
                B2B Pipeline
              </span>
            </h1>
            <p className="text-base md:text-lg lg:text-xl text-white/90 max-w-2xl mx-auto leading-relaxed">
              Enterprise-grade demand generation for companies serious about revenue growth.
            </p>
          </div>

          {/* Two Column: Mini ROI + CTA */}
          <div className="grid lg:grid-cols-2 gap-6 mb-10">
            {/* Mini ROI Calculator */}
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-5 md:p-6 border border-white/20">
              <div className="flex items-center gap-2 mb-4">
                <Calculator className="w-4 h-4 text-secondary" />
                <span className="text-white font-semibold text-sm">Quick ROI Preview</span>
              </div>
              
              <div className="space-y-4">
                <div>
                  <div className="flex justify-between text-xs mb-2">
                    <span className="text-white/70">Avg Deal Size</span>
                    <span className="text-white font-medium">£{dealSize[0].toLocaleString()}</span>
                  </div>
                  <Slider
                    value={dealSize}
                    onValueChange={setDealSize}
                    min={5000}
                    max={100000}
                    step={5000}
                    className="w-full"
                  />
                </div>
                
                <div>
                  <div className="flex justify-between text-xs mb-2">
                    <span className="text-white/70">Monthly Leads</span>
                    <span className="text-white font-medium">{monthlyLeads[0]}</span>
                  </div>
                  <Slider
                    value={monthlyLeads}
                    onValueChange={setMonthlyLeads}
                    min={5}
                    max={50}
                    step={5}
                    className="w-full"
                  />
                </div>
              </div>

              <div className="mt-4 pt-4 border-t border-white/10">
                <div className="flex items-center justify-between">
                  <span className="text-white/70 text-xs">Potential Annual Impact</span>
                  <span className="text-2xl md:text-3xl font-bold text-secondary">
                    +{formatCurrency(annualImpact)}
                  </span>
                </div>
                <Link 
                  to="/homev2#roi-calculator" 
                  className="text-xs text-secondary/80 hover:text-secondary mt-1 inline-block"
                >
                  See full calculator →
                </Link>
              </div>
            </div>

            {/* CTA Card */}
            <div className="bg-gradient-to-br from-secondary/20 to-accent/20 backdrop-blur-sm rounded-2xl p-5 md:p-6 border border-secondary/30 flex flex-col justify-between">
              <div>
                <h3 className="text-white font-bold text-lg mb-2">Ready to accelerate?</h3>
                <p className="text-white/80 text-sm mb-4">
                  Get a custom growth roadmap with projected pipeline impact for your business.
                </p>
              </div>
              
              <div className="space-y-3">
                <Button
                  asChild
                  size="lg"
                  className="bg-secondary hover:bg-secondary/90 text-primary font-bold group w-full"
                >
                  <Link to="/do-you-qualify">
                    Schedule Strategic Review
                    <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                  </Link>
                </Button>
                <Button
                  asChild
                  variant="outline"
                  size="lg"
                  className="bg-white/5 border-white/20 text-white hover:bg-white/10 w-full"
                >
                  <Link to="/enterprise">
                    Explore Enterprise Solutions
                  </Link>
                </Button>
              </div>
            </div>
          </div>


          {/* Trust Line */}
          <div className="text-center pt-6 border-t border-white/10">
            <p className="text-white/60 text-sm">
              Trusted by B2B companies across SaaS, Professional Services, and Enterprise Technology
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroB2B;
